"use client";

import {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { DevicePlatform } from "@speakai/shared";
import * as Sentry from "@sentry/react";
import { apiClient } from "@/lib/api/client";
import * as authApi from "@/features/auth/api";
import { AUTH_BROADCAST_CHANNEL, AUTH_COOKIE_NAMES, getBrowserCookieValue } from "@/lib/auth/cookies";
import { ROUTE_SESSION_COOKIE_NAME, buildLoginRedirectUrl } from "@/lib/auth/route-policy";
import {
  clearLegacyAuthStorage,
  clearPendingTwoFactorToken,
  getDeviceId,
  hasPendingTwoFactorToken,
  setLastUsedAuthType,
  setPendingTwoFactorToken,
  triggerCrossTabLogoutFallback,
} from "@/lib/auth/storage";
import { STORAGE_KEYS } from "@/config/constants";
import { setCurrentPlan, trackFunnel, trackFailure } from "@/lib/integrations/sentry-tracking";
import { trackActivity } from "@/lib/integrations/activity-events";
import { clearSentryUserContext, setSentryUserContext } from "@/lib/integrations/sentry-user-context";
import { pushDataLayer, setGa4UserId, trackLogin } from "@/lib/integrations/ga4";
import type { LoginResponse, UserProfile } from "@/features/auth/api";
import { getClientIP } from "@/lib/utils/ip";
import { releaseBrowserSession } from "@/lib/meeting-reminders/browser-reminders";
import { deleteUserTakes } from "@/features/recorder/take-store";
import { useIntercom } from "@/hooks/use-intercom";
import { OnboardingGoals } from "@/features/onboarding/components/onboarding-questions";
import { AuthContext } from "./auth-context";

const ONBOARDING_GOAL_VALUES: ReadonlySet<string> = new Set(
  Object.values(OnboardingGoals)
);

// Back-compat re-exports so existing imports from "@/providers/auth-provider" keep working.
export { useAuth } from "./auth-context";
export type { AuthContextValue } from "./auth-context";

type AuthBroadcastEvent =
  | { type: "logout" }
  | { type: "session-revoked" };

// Module-level dedup so Sentry receives at most one warning per browser session
// when companyId is absent on a hydrated profile. Resets on hard reload only.
let companyIdWarnedThisSession = false;

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const initRef = useRef(false);
  const refreshPromiseRef = useRef<Promise<void> | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const { boot: intercomBoot, shutdown: intercomShutdown } = useIntercom();

  const clearAuthenticatedSession = useCallback((preserveReturnUrl = true, { hardRedirect = true }: { hardRedirect?: boolean } = {}) => {
    // Abort in-flight requests so a late response can't repopulate the cache
    // after logout. The cache itself is wiped on the next login (race-free,
    // since the authenticated tree is unmounted then); clearing it here while
    // those screens are still mounted would trigger a refetch storm.
    queryClient.cancelQueries();
    // Cut off reminders before the session is cleared so nothing shows after sign-out.
    void releaseBrowserSession();
    clearLegacyAuthStorage();
    intercomShutdown();
    setUser(null);
    setIsLoggedIn(false);
    setIsLoading(false);
    clearSentryUserContext();
    setCurrentPlan("none");
    if (typeof document !== "undefined") {
      const isProd = import.meta.env.PROD;
      document.cookie = `${ROUTE_SESSION_COOKIE_NAME}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
      document.cookie = `${AUTH_COOKIE_NAMES.csrf}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax${isProd ? "; Secure" : ""}`;
      // spk_sid is httpOnly; only a hard nav can break out of the broken page.
      // Exempt public unauthenticated routes so logged-out visitors aren't bounced to login.
      const pathname = window.location.pathname;
      const isPublicPath = pathname.startsWith("/auth/");
      if (hardRedirect && typeof window !== "undefined" && !isPublicPath) {
        const { search } = window.location;
        const target = (!preserveReturnUrl || pathname === "/")
          ? "/auth/login"
          : buildLoginRedirectUrl(`${pathname}${search}`);
        window.location.replace(target);
      }
    }
  }, [intercomShutdown, queryClient]);

  const hydrateTrackingContext = useCallback((profile: UserProfile) => {
    setUser(profile);
    setIsLoggedIn(true);
    setIsLoading(false);

    // Sync backend-stored appearance into localStorage so ThemeApplier
    // (which reads from localStorage on every route change) picks up the
    // user's theme/font on a fresh device or after sign-in.
    if (typeof window !== "undefined" && profile.appearance) {
      if (profile.appearance.theme) {
        localStorage.setItem("app-theme", profile.appearance.theme);
      }
      if (profile.appearance.font) {
        localStorage.setItem("app-font", profile.appearance.font);
      }
    }

    setSentryUserContext(profile);
    if (profile.companyId) {
      setGa4UserId(profile.companyId);
    } else if (!companyIdWarnedThisSession) {
      companyIdWarnedThisSession = true;
      Sentry.captureMessage("companyId_unavailable_for_tracking", "warning");
    }
    const goal = profile.onboarding?.goal;
    Sentry.setTag(
      "signup_goal",
      goal && ONBOARDING_GOAL_VALUES.has(goal) ? goal : "unknown"
    );
    setCurrentPlan(profile.subscription?.planId || "none");
  }, []);

  const loadUserProfile = useCallback(async () => {
    const sessionResponse = await authApi.getSession();

    if (sessionResponse.success && !sessionResponse.data?.authenticated) {
      const refreshed = await apiClient.refreshSession();
      if (refreshed) {
        const profileResponse = await authApi.getUserByToken();
        if (profileResponse.success && profileResponse.data) {
          clearPendingTwoFactorToken();
          hydrateTrackingContext(profileResponse.data);
          return;
        }
      }
    }

    const profileResponse = await authApi.getUserByToken();
    if (profileResponse.success && profileResponse.data) {
      clearPendingTwoFactorToken();
      hydrateTrackingContext(profileResponse.data);
      return;
    }

    clearAuthenticatedSession();
  }, [clearAuthenticatedSession, hydrateTrackingContext]);

  const refreshUser = useCallback(async () => {
    if (hasPendingTwoFactorToken()) {
      setIsLoggedIn(false);
      setIsLoading(false);
      return;
    }

    if (refreshPromiseRef.current) {
      await refreshPromiseRef.current;
      return;
    }

    const promise = loadUserProfile();
    refreshPromiseRef.current = promise;

    try {
      await promise;
    } finally {
      if (refreshPromiseRef.current === promise) {
        refreshPromiseRef.current = null;
      }
    }
  }, [loadUserProfile]);

  const broadcast = useCallback((event: AuthBroadcastEvent) => {
    channelRef.current?.postMessage(event);
    triggerCrossTabLogoutFallback();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      channelRef.current = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
      channelRef.current.onmessage = (message: MessageEvent<AuthBroadcastEvent>) => {
        if (message.data?.type === "logout") {
          void releaseBrowserSession();
          clearLegacyAuthStorage();
          window.location.href = "/auth/login";
        } else if (message.data?.type === "session-revoked") {
          clearAuthenticatedSession();
          navigate("/auth/login");
        }
      };
    } catch {
      channelRef.current = null;
    }

    return () => {
      channelRef.current?.close();
      channelRef.current = null;
    };
  }, [clearAuthenticatedSession, navigate]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === "event-logout") {
        // Cross-tab fallback for a voluntary logout — send to a clean login, not a returnUrl deep link.
        clearAuthenticatedSession(false);
        navigate("/auth/login");
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [clearAuthenticatedSession, navigate]);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    apiClient.setDeviceId(getDeviceId());
    apiClient.setPlatform(DevicePlatform.WEB);

    if (!import.meta.env.PROD) {
      const cookieSession = getBrowserCookieValue(AUTH_COOKIE_NAMES.sessionPresence);
      const storedToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const storedRefresh = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

      if (cookieSession && (storedToken || storedRefresh)) {
        clearLegacyAuthStorage();
      } else {
        if (storedToken) apiClient.setToken(storedToken);
        if (storedRefresh) apiClient.setRefreshToken(storedRefresh);
      }
    }

    // Schedule proactive refresh
    const expiryFromCookie = getBrowserCookieValue(AUTH_COOKIE_NAMES.accessTokenExpiry);
    if (expiryFromCookie) {
      apiClient.notifyTokenExpiry(expiryFromCookie);
    }

    if (import.meta.env.PROD) {
      getClientIP()
        .then((ip) => apiClient.setIpAddress(ip))
        .catch(() => { });
    }

    startTransition(() => {
      void refreshUser();
    });
  }, [refreshUser]);

  // Keyed on the user id: a profile refresh must not re-boot (shutdown plus boot) the Messenger.
  const userId = user?._id;
  useEffect(() => {
    if (!userId) return;
    void intercomBoot();
  }, [userId, intercomBoot]);

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await authApi.login({
        email,
        password,
        deviceType: DevicePlatform.WEB,
        deviceId: getDeviceId(),
      });

      if (!response.success || !response.data) {
        // An unverified account is not a failed login: the server has sent a code, so send
        // the user to the same OTP screen register uses instead of a dead-end error.
        if (response.errorCode === "AUTH_OTP_REQUIRED") {
          return { success: true, otpRequired: true };
        }
        trackFailure("auth", "login_fail", {
          method: "email",
          error_reason: response.error,
        });
        return {
          success: false,
          error: response.error || "Invalid email or password",
        };
      }

      const data = response.data;

      if (data.isTwoFactorRequired) {
        setPendingTwoFactorToken("pending");
        return { success: true, twoFactorRequired: true };
      }

      if (data.isOtpRequired) {
        return { success: true, otpRequired: true };
      }

      clearPendingTwoFactorToken();
      setLastUsedAuthType("password");

      if (data.accessToken) {
        apiClient.setToken(data.accessToken);
        if (!import.meta.env.PROD) {
          localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken);
        }
      }
      if (data.refreshToken) {
        apiClient.setRefreshToken(data.refreshToken);
        if (!import.meta.env.PROD) {
          localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
        }
      }

      // Wipe any previous user's cached data before hydrating this one, so an
      // account switch never shows the prior user's data. Race-free here: the
      // authenticated tree is unmounted (we're on the login page), so no mounted
      // observer refetches.
      queryClient.clear();
      await refreshUser();

      if (data.tokenExpiresIn) {
        apiClient.notifyTokenExpiry(data.tokenExpiresIn);
      }

      trackFunnel("auth", "login", { method: "email" });
      trackActivity("auth.sign_in", { method: "password" });
      trackLogin("email");
      pushDataLayer("Authentication", "Login", "email");

      return { success: true };
    },
    [refreshUser, queryClient]
  );

  const loginWithTokens = useCallback(
    async (data: LoginResponse) => {
      clearPendingTwoFactorToken();
      // Wipe any previous user's cached data before hydrating (SSO / 2FA / token path).
      queryClient.clear();
      await refreshUser();
      if (data.tokenExpiresIn) {
        apiClient.notifyTokenExpiry(data.tokenExpiresIn);
      }
      trackActivity("auth.sign_in", { method: "token" });
    },
    [refreshUser, queryClient]
  );

  const signedInUserId = user?._id;
  const logout = useCallback(async () => {
    try {
      // Release first: the device row needs the still-valid session to be deactivated.
      await releaseBrowserSession();
      await authApi.logout();
    } catch {
      console.error("User logout error")
    }

    if (signedInUserId) {
      void deleteUserTakes(signedInUserId).catch((err) => Sentry.captureException(err, { tags: { feature: "recorder" } }));
    }
    broadcast({ type: "logout" });
    trackActivity("auth.sign_out");
    clearAuthenticatedSession(true, { hardRedirect: false });
  }, [broadcast, clearAuthenticatedSession, signedInUserId]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoggedIn,
        isLoading,
        login,
        loginWithTokens,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

